import { useEffect, useState } from "react";
import { useStore } from "../hooks.ts";
import { compareLagers } from "../brand.ts";
import { a1Times, clearA1, median } from "../timing.ts";

interface StorageInfo {
  persisted: boolean | null;
  usage: number | null;
  quota: number | null;
}

const lagerName = (name: unknown, id: string) => (typeof name === "string" && name !== "" ? name : id);

/** What the device holds and whether it is in sync; storage kept by the browser? (P0-5) */
export function Status() {
  const { store } = useStore();
  const [storage, setStorage] = useState<StorageInfo>({ persisted: null, usage: null, quota: null });
  const r = store.replica;
  const s = store.status;
  const nameOf = (id: string) => lagerName(r?.entityView().get(`lager|${id}`)?.fields.name, id);

  async function refresh() {
    const persisted = (await navigator.storage?.persisted?.()) ?? null;
    const est = await navigator.storage?.estimate?.();
    setStorage({ persisted, usage: est?.usage ?? null, quota: est?.quota ?? null });
  }
  useEffect(() => void refresh(), []);

  const a1 = a1Times();
  const seconds = (ms: number) => `${(ms / 1000).toLocaleString("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
  const standalone = window.matchMedia("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
  // What a household needs (B13); the measurements of the spike folded away under „Für Entwickler“.
  const rows: [string, string][] = [
    ["Netz", s.online ? "online" : "offline"],
    ["Wartende Änderungen", String(r?.pending.length ?? 0)],
    ["Letzter Abgleich", s.lastSync ? new Date(s.lastSync).toLocaleString("de-DE") : "–"],
    // Names, not ids (B13; KellerLog's B8): „Bar“, not „bar“.
    ["Lager", [...(store.config?.lagers ?? [])].sort(compareLagers(nameOf)).map(nameOf).join(", ")],
  ];
  const developer: [string, string][] = [
    ["Stand (Nummer)", String(r?.seq ?? 0)],
    ["Stichtagsstand", r?.snapshot ? new Date(r.snapshot.cutoff).toLocaleDateString("de-DE") : "–"],
    ["Buchungen auf dem Gerät", String(r?.journal().length ?? 0)],
    ["Erstes Laden", s.lastLoadMs !== null ? `${s.lastLoadMs} ms` : "–"],
    ["Öffnen bis Entnahme (A1)", a1.length ? `zuletzt ${seconds(a1.at(-1)!)} · Median ${seconds(median(a1)!)} · ${a1.length} ${a1.length === 1 ? "Messung" : "Messungen"}` : "–"],
    ["Als App installiert", standalone ? "ja" : "nein (im Browser)"],
    ["Speicher dauerhaft", storage.persisted === null ? "unbekannt" : storage.persisted ? "ja" : "nein"],
    ["Speicher belegt", storage.usage !== null ? `${(storage.usage / 1e6).toFixed(1)} MB von ${((storage.quota ?? 0) / 1e9).toFixed(1)} GB` : "–"],
  ];
  const table = (list: [string, string][]) => (
    <table>
      <tbody>
        {list.map(([k, v]) => (
          <tr key={k}>
            <th>{k}</th>
            <td>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div className="status">
      {table(rows)}
      {s.lastError && <p className="error">{s.lastError}</p>}
      {r && r.rejected.length > 0 && (
        <>
          <h3>Abgelehnt</h3>
          <ul className="list">
            {r.rejected.map((x, i) => (
              <li key={i}>{x.error}</li>
            ))}
          </ul>
        </>
      )}
      <div className="buttons">
        <button onClick={() => store.kick()}>Jetzt abgleichen</button>
        <button
          className="danger"
          onClick={() => {
            if (confirm("Lokale Daten dieses Geräts löschen? Nicht abgeglichene Änderungen gehen verloren.")) void store.signOut();
          }}
        >
          Abmelden
        </button>
      </div>
      <details className="developer" data-testid="developer">
        <summary>Für Entwickler</summary>
        {table(developer)}
        <div className="buttons">
          <button
            onClick={async () => {
              await navigator.storage?.persist?.();
              await refresh();
            }}
          >
            Speicher dauerhaft anfragen
          </button>
          <a className="button" href="#/messung">
            Messung im Browser
          </a>
          <button
            onClick={() => {
              clearA1();
              void refresh();
            }}
          >
            A1-Zeiten löschen
          </button>
        </div>
      </details>
    </div>
  );
}
